import React, { useEffect, useState } from 'react'
import { Calendar as ReactCalendar } from 'react-big-calendar'
import 'moment/locale/en-gb'
import { useDispatch, useSelector } from 'react-redux'

import { Button, Checkbox } from 'components/UI'
import CalendarTitle from './CalendarTitle'
import TimeSlotWrapper from './TimeSlotWrapper'
import WeekDaysHeader from './WeekDaysHeader'
import DropDown from 'components/Input/DropDown'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'

import {
  fetchAvailableEvents,
  addNewAvailabilities,
  setCreateBannerStep,
} from 'routing/fetchingfunction/user/user.action'
import {
  selectAvailableEventsIsFetching,
  selectAvailableEventsIsSubmitting,
  selectAvailableEventsNewCreated,
  selectLastCreatedBanner,
  selectUserData,
} from 'routing/fetchingfunction/user/user.reducer'

import { useMainCategories } from '../../../../../../hooks'

import { advanceOptions, localizer } from '../../../../../../constants'

import './calendar.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

const Calendar = ({ onCreate, isEdit }) => {
  const dispatch = useDispatch()

  const userData = useSelector(selectUserData)
  const isLoading = useSelector(selectAvailableEventsIsFetching)
  const isSubmitting = useSelector(selectAvailableEventsIsSubmitting)
  const newCreatedEvents = useSelector(selectAvailableEventsNewCreated)

  const bannerCreated = useSelector(selectLastCreatedBanner)

  const mainCategories = useMainCategories()

  const [advanceBooking, setAdvanceBooking] = useState(
    userData.calendar_settings?.advance_booking || ''
  )
  const [repeatWeekly, setRepeatWeekly] = useState(
    userData.calendar_settings?.repeat_weekly || false
  )
  const [advanceBookingErrors, setAdvanceBookingErrors] = useState(null)

  const onAdvanceChange = (value) => {
    setAdvanceBooking(value)
  }

  const onRepeatChange = (e) => {
    setRepeatWeekly(e.target.checked)
  }

  const handleBack = () => {
    dispatch(setCreateBannerStep(1))
  }

  const handleSubmit = () => {
    if (!advanceBooking) {
      return setAdvanceBookingErrors('Please select a value')
    }

    setAdvanceBookingErrors(null)

    dispatch(
      addNewAvailabilities({
        availability_calendars: newCreatedEvents,
        calendar_settings: {
          repeat_weekly: repeatWeekly,
          advance_booking: advanceBooking,
        },
      })
    ).then((data) => {
      if (!data.error) {
        dispatch(setCreateBannerStep(isEdit ? 4 : 3))
        onCreate?.()
      }
    })
  }

  useEffect(() => {
    dispatch(fetchAvailableEvents())
  }, [])

  return (
    <div className="relative">
      {isLoading ? (
        <div className="min-h-[600px] flex items-center justify-center">
          <div
            className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] animate-spinInfinite"
            role="status"
          >
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Loading...
            </span>
          </div>
        </div>
      ) : (
        <>
          <ReactCalendar
            className="metaverse-calendar"
            defaultView="week"
            startAccessor="start"
            endAccessor="end"
            localizer={localizer}
            defaultDate={new Date()}
            style={{ height: '600px', width: '100%' }}
            components={{
              toolbar: CalendarTitle,
              timeSlotWrapper: (props) => (
                <TimeSlotWrapper {...props} mainCategories={mainCategories} />
              ),
              header: WeekDaysHeader,
            }}
            timeslots={1}
            step={60}
          />
          <div className="py-3 flex items-center gap-2">
            <p className="text-sm">How far in advance can students book a class</p>
            <DropDown
              className="!w-[200px]"
              menuItems={advanceOptions}
              setValue={onAdvanceChange}
              placeholder="Select Months"
              value={advanceBooking}
            />
            <Checkbox checked={repeatWeekly} onChange={onRepeatChange} label="Repeat every week" />
          </div>
          {advanceBookingErrors && <ErrorMessageWithExclamation text={advanceBookingErrors} />}
          <div className="flex items-center justify-between py-2">
            <p>*You can’t work for more than 40 hours per week</p>
            <div className="flex gap-2">
              <button
                onClick={handleBack}
                className="rounded-lg px-6 bg-white text-[#F79009] border-[1px] border-[#F79009] font-semibold hover:text-white hover:!border-primary-button-hover hover:!bg-primary-button-hover transition-colors duration-200 ease"
              >
                Back to Class Form
              </button>

              <Button
                className="px-6 min-w-[160px] font-semibold"
                onClick={handleSubmit}
                isLoading={isSubmitting}
              >
                Save & Continue
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(Calendar)
